import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { useForm } from '@tanstack/react-form'
import { Loader2, Mail } from 'lucide-react'
import { z } from 'zod'
import { resetForgotPasswordAuthForgotPasswordPostMutation } from '@/client/@tanstack/react-query.gen'
import { useMutation } from '@tanstack/react-query'

export const Route = createFileRoute('/_login_forms/_forgot_password/forgot-password')({
  beforeLoad: () => ({
    title: 'Reset Password',
    description: 'Enter your email below to receive a password reset link.',
  }),
  component: ForgotPasswordForm,
})

function ForgotPasswordForm() {
  const navigate = useNavigate()
  const { toast } = useToast()

  const resetPassword = useMutation({
    ...resetForgotPasswordAuthForgotPasswordPostMutation(),
    onSuccess: (_data, variables) => {
      navigate({
        to: '/forgot-password-sent',
        search: { email: variables.body.email },
      })
    },
    onError: (error) => {
      if (error instanceof Error) {
        toast({ title: error.message, variant: 'destructive' })
      }
      else {
        toast({ title: 'An unexpected error occurred', variant: 'destructive' })
      }
    },
  })

  const form = useForm({
    defaultValues: { email: '' },
    validators: {
      onChange: ({ value }) => {
        const result = z.object({ email: z.string().email() }).safeParse(value)
        return result.success
          ? undefined
          : result.error.issues.map(index => index.message).join(', ')
      },
    },
    onSubmit: async ({ value }) => {
      await resetPassword.mutateAsync({
        body: {
          email: value.email,
        },
      })
    },
  })

  return (
    <div>
      <form
        id="forgot-password-form"
        className="space-y-6"
        onSubmit={(event) => {
          event.preventDefault()
          form.handleSubmit()
        }}
      >
        <FieldGroup>
          {/* Email Input */}
          <form.Field
            name="email"
            children={(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor="email" className="text-base">Email</FieldLabel>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="email"
                      name={field.name}
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={event_ => field.handleChange(event_.target.value)}
                      aria-invalid={isInvalid}
                      disabled={form.baseStore.state.isSubmitting}
                      className="pl-12"
                      required
                    />
                  </div>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          />
        </FieldGroup>

        {/* Reset Password Button */}
        <Button
          type="submit"
          form="forgot-password-form"
          variant="default"
          className="w-full h-11 text-base"
          disabled={form.baseStore.state.isSubmitting}
        >
          {form.baseStore.state.isSubmitting
            ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Sending...
                </>
              )
            : (
                'Send Password Reset Link'
              )}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={form.baseStore.state.isSubmitting}
          onClick={() => navigate({ to: '/login' })}
        >
          Back to Login
        </Button>
      </form>
    </div>
  )
}
